import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ClosingEffectiveness, CloseSignalResult } from '../types';
import SectionShell, { CLAMP_THREE_LINES, Figure } from './SectionShell';

const OUTCOME_COLOR: Record<CloseSignalResult['outcome'], 'success' | 'warning' | 'error' | 'default'> = {
  converted: 'success',
  partial: 'warning',
  missed: 'error',
  pending: 'default',
};

const OUTCOME_LABEL: Record<CloseSignalResult['outcome'], string> = {
  converted: 'converted',
  partial: 'partly used',
  missed: 'missed',
  // The call ended before the rep responded to it (owner, 2026-09-27: same words as the rep's end screen).
  pending: 'not answered',
};

/** The buying signals the customer offered and what the rep did with each. */
const ClosingEffectivenessSection: React.FC<{ data?: ClosingEffectiveness }> = ({ data }) => {
  if (!data) return null;
  return (
    <SectionShell title="Closing" score={data.overallScore} summary={data.overallSummary}>
      <Stack direction="row" spacing={4} mb={data.signals?.length ? 2 : 0} flexWrap="wrap" useFlexGap>
        <Figure label="Asked for a next step" value={data.pushedForClose ? 'Yes' : 'No'} />
        <Figure label="Buying signals" value={data.signalsOffered} />
        <Figure label="Converted" value={data.converted} />
      </Stack>
      <Stack spacing={1.5}>
        {data.signals?.map((signal, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={signal.repResponse ? 1 : 0}>
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>{signal.type} signal</Typography>
              <Chip label={OUTCOME_LABEL[signal.outcome] ?? signal.outcome} size="small" color={OUTCOME_COLOR[signal.outcome] ?? 'default'} />
            </Box>
            {signal.repResponse && (
              <Typography variant="body2" color="text.secondary" sx={CLAMP_THREE_LINES}>
                Rep: {signal.repResponse}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    </SectionShell>
  );
};

export default ClosingEffectivenessSection;
